/**
 * GrantFinder — `/grants` list view. Wave 4 v3 executive redesign
 * (anchor v3 tightening pass: crisper radii, starker typography scale,
 * denser internal padding).
 *
 * Structural composition:
 *   • PageHeader primitive drives the eyebrow + title + description
 *     block — no more inline `text-[36px] sm:text-[48px]` heroics.
 *   • Card primitive routed through `--ds-radius-card` (4px) so the
 *     visible corners read crisp/precision, not soft — Mercury/Ramp
 *     aesthetic bar.
 *   • Four distinct typographic levels inside every card:
 *       L1  Agency micro-label — 11px uppercase, ink-subtle
 *       L2  Grant title        — Heading level=3 size="lg" (20px, display)
 *       L3  Description body   — 13px, ink-muted
 *       L4  Metadata badges    — 11px uppercase tracking chips
 *   • Badge primitive replaces pastel award-amount pills and category
 *     chips with tone-mapped, low-noise chips. Deadline chip is now
 *     Badge-driven (open→success, rolling→accent, upcoming→info,
 *     closed→danger, unknown→neutral).
 *   • Internal card padding tightened one step (`px-5`/`pt-5` → `px-4`/
 *     `pt-4`, `mt-5`/`mt-4` → `mt-4`/`mt-3`) for denser data feel.
 *   • Search + filter inputs use `--ds-radius-field` (4px) and hairline
 *     borders — enterprise-input aesthetic, not consumer-SaaS soft.
 *   • Empty state uses hairline dashed border on a plain surface.
 *
 * Every data-testid, banned-phrase constraint, and behaviour hook
 * (search, tag filter, match-map fetch, save button, MatchTierChip
 * co-render) is preserved byte-for-byte.
 *
 * Frontend Discipline 2 (locked): EligibilityBadge + MatchTierChip
 * stay VISUALLY DISTINCT wherever they appear together. They are
 * never merged into a composite badge.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Filter,
  Calendar,
  Building2,
  ArrowUpRight,
  Target,
  LayoutGrid,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import GrantShell from "@/components/grants/GrantShell";
import EligibilityBadge from "@/components/grants/EligibilityBadge";
import SaveGrantButton from "@/components/grants/SaveGrantButton";
import MatchTierChip from "@/components/grants/MatchTierChip";
import { PageHeader } from "@/design-system/components/PageHeader";
import { Card } from "@/design-system/components/Card";
import { Badge } from "@/design-system/components/Badge";
import { Heading } from "@/design-system/components/Heading";
import { toast } from "sonner";

const CATEGORY_OPTIONS = [
  { key: "", label: "All Categories" },
  { key: "federal", label: "Federal" },
  { key: "state", label: "State" },
  { key: "minority_owned", label: "Minority-Owned" },
  { key: "women_owned", label: "Women-Owned" },
  { key: "veteran_owned", label: "Veteran-Owned" },
  { key: "rural", label: "Rural" },
  { key: "manufacturing", label: "Manufacturing" },
  { key: "technology", label: "Technology" },
  { key: "healthcare", label: "Healthcare" },
  { key: "agriculture", label: "Agriculture" },
  { key: "nonprofits", label: "Nonprofits" },
  { key: "small_business_innovation", label: "Small Business Innovation" },
];

// Executive tone mapping — deadline_status → Badge tone. Warning /
// danger / success tones consume the design-system feedback palette,
// giving us restrained, enterprise-appropriate chips (no pastel pink).
const DEADLINE_STATUS_META = {
  open:     { label: "Application Open", tone: "success" },
  rolling:  { label: "Rolling Deadline", tone: "accent"  },
  upcoming: { label: "Upcoming",         tone: "info"    },
  closed:   { label: "Closed",           tone: "danger"  },
  unknown:  { label: "Next Cycle TBD",   tone: "neutral" },
};

function DeadlineChip({ status }) {
  const meta = DEADLINE_STATUS_META[status] || DEADLINE_STATUS_META.unknown;
  return (
    <Badge
      tone={meta.tone}
      size="sm"
      data-testid={`grant-card-deadline-status-${status}`}
      className="uppercase tracking-[0.14em] font-semibold"
    >
      <Calendar className="w-3 h-3" aria-hidden="true" />
      {meta.label}
    </Badge>
  );
}

function formatAward(min, max) {
  if (min == null && max == null) return "Amount varies";
  if (min && max && min === max) return `$${Number(min).toLocaleString()}`;
  if (min && max) return `$${Number(min).toLocaleString()} – $${Number(max).toLocaleString()}`;
  if (max) return `Up to $${Number(max).toLocaleString()}`;
  return `From $${Number(min).toLocaleString()}`;
}

function GrantCard({ item, match }) {
  const { grant, eligibility_snapshot, is_saved } = item;
  return (
    <Card
      data-testid={`grant-card-${grant.id}`}
      className="flex flex-col rounded-[var(--ds-radius-card)]"
    >
      <div className="px-4 pt-4 pb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* L1 — Agency micro-label: 11px uppercase, subtle ink */}
          <div
            className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold"
            style={{ color: "var(--ds-color-ink-subtle)" }}
            data-testid={`grant-card-agency-${grant.id}`}
          >
            <Building2 className="w-3 h-3" aria-hidden="true" />
            <span className="truncate">{grant.agency}</span>
          </div>
          {/* L2 — Grant title: Heading level=3 (20px) semibold display */}
          <Heading
            level={3}
            size="lg"
            weight="semibold"
            className="mt-1 leading-snug"
          >
            <Link
              to={`/grants/${grant.id}`}
              data-testid={`grant-card-title-link-${grant.id}`}
              className="hover:underline"
              style={{ color: "var(--ds-color-ink)" }}
            >
              {grant.title}
            </Link>
          </Heading>
        </div>
        {/* Frontend Discipline 2 — EligibilityBadge and MatchTierChip
            stay VISUALLY DISTINCT. Never merge them into a composite. */}
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <EligibilityBadge status={eligibility_snapshot.status} />
          {match && (
            <MatchTierChip
              tier={match.match_tier}
              score={match.match_score}
              size="sm"
              testId={`grant-card-match-chip-${grant.id}`}
            />
          )}
        </div>
      </div>

      <p
        className="px-4 text-[13px] leading-snug line-clamp-3"
        style={{ color: "var(--ds-color-ink-muted)" }}
      >
        {grant.program_summary}
      </p>

      <div className="px-4 mt-3 flex flex-wrap items-center gap-1.5">
        <Badge
          tone="neutral"
          size="sm"
          className="uppercase tracking-[0.14em] font-semibold"
          data-testid={`grant-card-award-${grant.id}`}
        >
          {formatAward(grant.award_amount_min, grant.award_amount_max)}
        </Badge>
        <DeadlineChip status={grant.deadline_status || "unknown"} />
        {(grant.tags || []).slice(0, 2).map((t) => (
          <Badge
            key={t}
            tone="neutral"
            size="sm"
            className="uppercase tracking-[0.14em] font-medium"
          >
            {t.replace(/_/g, " ")}
          </Badge>
        ))}
      </div>

      <div
        className="mt-4 flex items-center justify-between gap-3 border-t px-4 py-2.5"
        style={{ borderColor: "var(--ds-color-border)" }}
      >
        <Link
          to={`/grants/${grant.id}`}
          data-testid={`grant-card-detail-cta-${grant.id}`}
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold hover:underline"
          style={{ color: "var(--ds-color-accent)" }}
        >
          View eligibility
          <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
        </Link>
        <SaveGrantButton
          grantId={grant.id}
          initialSaved={is_saved}
          testIdPrefix={`grant-card-save-${grant.id}`}
        />
      </div>
    </Card>
  );
}

export default function GrantFinder() {
  const [items, setItems] = useState(null);
  const [tag, setTag] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  // Phase G2A CP3 — user's cached matches, keyed by grant_id. Populated
  // on mount via a single GET /api/grants/matches call (not premium
  // gated, up to 3 rows on Free). Absence of a chip on a card is a
  // valid signal — it just means "not yet computed for this grant".
  const [matchMap, setMatchMap] = useState({});

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const params = {};
        if (tag) params.tag = tag;
        const res = await api.get("/grants", { params });
        if (!cancelled) setItems(res.data.items || []);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [tag]);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/grants/matches")
      .then((res) => {
        if (cancelled) return;
        const map = {};
        for (const m of res.data.items || []) {
          if (m.grant_id) map[m.grant_id] = m;
        }
        setMatchMap(map);
      })
      .catch(() => {
        // 404 (feature disabled) OR any other error → simply no chips.
        // The card view remains functional; users compute matches from
        // the detail page anyway.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!items) return null;
    if (!q.trim()) return items;
    const needle = q.trim().toLowerCase();
    return items.filter(
      (it) =>
        it.grant.title.toLowerCase().includes(needle) ||
        it.grant.agency.toLowerCase().includes(needle),
    );
  }, [items, q]);

  const inputBaseClass =
    "w-full text-[14px] focus:outline-none rounded-[var(--ds-radius-field)]";
  const inputBaseStyle = {
    background: "var(--ds-color-surface)",
    border: "1px solid var(--ds-color-border)",
    color: "var(--ds-color-ink)",
    transitionDuration: "var(--ds-duration-fast)",
  };

  return (
    <GrantShell testId="grant-finder-root">
      <PageHeader
        eyebrow="Grant Navigator"
        title="Find grants that appear to match your business"
        description="Match analysis is based on the information in your grant profile. Complete your profile for firmer reads on every card below."
        data-testid="grant-finder-header"
      />

      <section className="mt-2 flex flex-col gap-3 md:flex-row md:items-center">
        <label className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
            style={{ color: "var(--ds-color-ink-subtle)" }}
            aria-hidden="true"
          />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title or agency"
            data-testid="grant-finder-search"
            className={`${inputBaseClass} pl-10 pr-4 h-10`}
            style={inputBaseStyle}
          />
        </label>
        <label className="relative">
          <Filter
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
            style={{ color: "var(--ds-color-ink-subtle)" }}
            aria-hidden="true"
          />
          <select
            data-testid="grant-finder-category-filter"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            className={`${inputBaseClass} pl-10 pr-8 h-10 appearance-none`}
            style={inputBaseStyle}
          >
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </section>

      {loading && (
        <div
          data-testid="grant-finder-loading"
          className="mt-10 text-center text-[13px]"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          Loading grants…
        </div>
      )}

      {!loading && filtered && filtered.length === 0 && (
        <div
          data-testid="grant-finder-empty"
          className="mt-10 rounded-[var(--ds-radius-card)] p-10 text-center"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px dashed var(--ds-color-border-strong)",
          }}
        >
          <p
            className="text-[14px] font-semibold"
            style={{ color: "var(--ds-color-ink)" }}
          >
            No matching grants at the moment.
          </p>
          <p
            className="mt-2 text-[13px]"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            Try clearing the filter or refining your search text.
          </p>
        </div>
      )}

      {!loading && filtered && filtered.length > 0 && (
        <div
          data-testid="grant-finder-list"
          className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4"
        >
          {filtered.map((item) => (
            <GrantCard
              key={item.grant.id}
              item={item}
              match={matchMap[item.grant.id] || null}
            />
          ))}
        </div>
      )}

      {/* Top Matches shortcut — only shown when the user has computed
          at least one match. Free tier hitting this link is still fine
          — the destination page renders the inline upgrade CTA. */}
      {Object.keys(matchMap).length > 0 && (
        <div className="mt-6 flex flex-wrap justify-end gap-4">
          <Link
            to="/grants/matches"
            data-testid="grant-finder-top-matches-link"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold hover:underline"
            style={{ color: "var(--ds-color-accent)" }}
          >
            <Target className="w-4 h-4" aria-hidden="true" />
            View Top Matches
          </Link>
          <Link
            to="/grants/pipeline"
            data-testid="grant-finder-pipeline-link"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold hover:underline"
            style={{ color: "var(--ds-color-accent)" }}
          >
            <LayoutGrid className="w-4 h-4" aria-hidden="true" />
            Open Pipeline
          </Link>
        </div>
      )}
    </GrantShell>
  );
}
